import cn from "classnames";
import ErrorBoundary from "components/shared/errorBoundary";
import Select, { defaultMultipleSelectProps, OptionWithFederalSubject, ValueContainer } from "components/shared/select";
import { userMessageTypesEnum } from "helpers/enums";
import { DateRangeColumnFilter, SelectColumnFilter } from "helpers/reactTable";
import { splitKeyFromProps } from "helpers/reactTableProps";
import { ymEvent } from "helpers/system";
import { useLocalStorage } from "hooks/useLocalStorage";
import { UmamiManager } from "lib/umami";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useExpanded, useFlexLayout, useResizeColumns, useTable } from "react-table";
import { namesList as counterpartiesList } from "sources/books/cargoOwners";
import { numbersList } from "sources/books/trucks";
import { getRegionsByName } from "sources/map";
import { Car, Counterparties, EndDates, Indicators, Notifications, Route } from "./cells";
import { ExpandedCar, ExpandedRoute } from "./expanded";
import HeaderCell from "./headerCell";
import NotificationsGear from "./notificationsGear";
import TooltipOverlay from "./tooltipOverlay";

const defaultNotificationsFilter = Object.keys(userMessageTypesEnum).reduce(
    (acc, key) => ({ ...acc, [key]: true }),
    {},
);

const Table = (props) => {
    const [columnWidths, setColumnWidths] = useLocalStorage("trip-planning-column-widths", {});
    const storeColumnWidth = ({ columnWidths: newColumnWidths }) =>
        setColumnWidths({ ...columnWidths, ...newColumnWidths });
    const [blurred, setBlurred] = useState(false);
    const [expandedRowColumn, setExpandedRowColumn] = useState();
    const scrollRef = useRef();

    useEffect(() => {
        scrollRef.current.scrollTop = 0;
    }, [props.start]);

    const { sorted, onSortedChange, accessPermits, modal } = props;

    const [notificationsFilter, setNotificationsFilter] = useLocalStorage(
        "trip-planning-notifications-filter",
        defaultNotificationsFilter,
    );

    const tableColumns = useMemo(() => {
        const items = [
            {
                Header: "№",
                id: "row",
                testId: "trip-planning-row-number",
                filterable: false,
                sticky: true,
                width: 50,
                Cell: ({ row, start }) => <div>{start + row.index + 1}</div>,
            },
            {
                Header: "ТС",
                accessor: "car",
                id: "car/id",
                testId: "trip-planning-car",
                sortable: true,
                filterable: true,
                clickable: true,
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            ...defaultMultipleSelectProps,
                            components: { ValueContainer },
                            inputProps: { name: "carNumber" },
                            url: numbersList,
                            propName: "number",
                            menuPortalTarget: document.body,
                            onMenuOpen: () => setTimeout(() => setBlurred(true), 0),
                            onMenuClose: () => setBlurred(false),
                        }}
                    />
                ),
                Cell: ({ row: { original } }) => <Car data={original} modal={modal} />,
                tooltipOverlay: (
                    <div>
                        Количество машин
                        <br />
                        Сортировка по запланированным машинам
                        <br />
                        Номер машины
                        <br />
                        Имя водителя
                        <br />
                        Контакты
                        <br />
                        Документы по сцепке
                        <br />
                        Информация ТО и Перецепке
                        <br />
                    </div>
                ),
                Sort: ({ column }) => {
                    useEffect(() => {
                        UmamiManager.track("VehicleColumnHeaderSortIconClicked");

                        setBlurred(true);

                        return () => setBlurred(false);
                    }, []);

                    const handleClickWrapper = (callback) => {
                        UmamiManager.track("VehicleColumnHeaderSortingFieldClicked");

                        callback();
                    };

                    return (
                        <div className="p-header__sort">
                            <div
                                className="p-header__sort-item"
                                onClick={() =>
                                    handleClickWrapper(() => onSortedChange(column, "car/numberDigitsOnly", "asc"))
                                }
                            >
                                По цифрам машины (от 0 до 9)
                            </div>
                            <div
                                className="p-header__sort-item"
                                onClick={() =>
                                    handleClickWrapper(() => onSortedChange(column, "car/numberDigitsOnly", "desc"))
                                }
                            >
                                По цифрам машины (от 9 до 0)
                            </div>
                            <div
                                className="p-header__sort-item"
                                onClick={() =>
                                    handleClickWrapper(() => onSortedChange(column, "car/numberLettersOnly", "asc"))
                                }
                            >
                                По буквам машины (от А до Я)
                            </div>
                            <div
                                className="p-header__sort-item"
                                onClick={() =>
                                    handleClickWrapper(() => onSortedChange(column, "car/numberLettersOnly", "desc"))
                                }
                            >
                                По буквам машины (от Я до А)
                            </div>
                            <div
                                className="p-header__sort-item"
                                onClick={() =>
                                    handleClickWrapper(() => onSortedChange(column, "driver/lastName", "asc"))
                                }
                            >
                                По фамилии водителя (от А до Я)
                            </div>
                            <div
                                className="p-header__sort-item"
                                onClick={() =>
                                    handleClickWrapper(() => onSortedChange(column, "driver/lastName", "desc"))
                                }
                            >
                                По фамилии водителя (от Я до А)
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: "Показатели",
                accessor: "mileage",
                testId: "trip-planning-indicators",
                Cell: ({ value }) =>
                    value && <Indicators {...(value ?? {})} currency={props.organization.currencyData?.name} />,
                tooltipOverlay: "Показатели по машине с начала  месяца",
            },
            {
                Header: "Контрагенты",
                accessor: "counterparties",
                testId: "trip-planning-counterparties",
                filterable: true,
                filterType: "select",
                isMulti: true,
                sortable: true,
                Filter: (props) => (
                    <SelectColumnFilter
                        {...props}
                        selectProps={{
                            ...defaultMultipleSelectProps,
                            components: { ValueContainer },
                            inputProps: { name: "counterpartyId" },
                            url: counterpartiesList,
                            propName: "name",
                            menuPortalTarget: document.body,
                            onMenuOpen: () => setTimeout(() => setBlurred(true), 0),
                            onMenuClose: () => setBlurred(false),
                        }}
                    />
                ),
                Cell: ({ value }) => <Counterparties data={value} />,
                tooltipOverlay: "Контрагенты",
                Sort: ({ column }) => {
                    useEffect(() => {
                        UmamiManager.track("VehicleColumnHeaderSortIconClicked");

                        setBlurred(true);

                        return () => setBlurred(false);
                    }, []);

                    const handleClickWrapper = (callback) => {
                        UmamiManager.track("CounterpartyColumnHeaderSortingFieldClicked");

                        callback();
                    };

                    return (
                        <div className="p-header__sort">
                            <div
                                className="p-header__sort-item"
                                onClick={() =>
                                    handleClickWrapper(() => onSortedChange(column, "firstCounterpartyName", "asc"))
                                }
                            >
                                По возрастанию
                            </div>
                            <div
                                className="p-header__sort-item"
                                onClick={() =>
                                    handleClickWrapper(() => onSortedChange(column, "firstCounterpartyName", "desc"))
                                }
                            >
                                По убыванию
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: "Маршрут",
                accessor: "to",
                testId: "trip-planning-route",
                filterable: true,
                clickable: true,
                Filter: ({ filtered, onFilteredChange, column }) => {
                    const selectProps = {
                        inputProps: { name: "to" },
                        url: getRegionsByName,
                        propName: "name",
                        minimumInputLength: 2,
                        components: { Option: OptionWithFederalSubject },
                        wrapper: "book-field",
                        onMenuOpen: () => setTimeout(() => setBlurred(true), 0),
                        onMenuClose: () => setBlurred(false),

                        onChange: (option) =>
                            onFilteredChange({
                                id: column.id,
                                value: option.value ? { id: option.option.id, type: option.option.type } : option.value,
                                filterType: column.filterType,
                                isMulti: column.isMulti,
                                option: option.option,
                            }),
                        value: filtered[column.id]?.value?.id,
                        menuPortalTarget: document.body,
                        bWrapper: "row",
                        styles: {
                            menu: (styles) => ({ ...styles, minWidth: "250px" }),
                            container: (styles) => ({ ...styles, width: "100%" }),
                            control: (styles) => ({ ...styles, minHeight: "26px", fontSize: "14px" }),
                            input: (styles) => ({
                                ...styles,
                                height: "17px",
                                paddingTop: 0,
                                marginTop: 0,
                                fontWeight: 400,
                            }),
                            singleValue: (styles) => ({
                                ...styles,
                                height: "18px",
                                lineHeight: "18px",
                                fontWeight: 400,
                                textAlign: "left",
                            }),
                            multiValue: (styles) => ({ ...styles, height: "18px", lineHeight: "14px" }),
                            valueContainer: (styles) => ({ ...styles, lineHeight: "18px", flexWrap: "nowrap" }),
                            indicatorsContainer: (styles) => ({ ...styles, height: "26px" }),
                            clearIndicator: (styles) => ({ ...styles, padding: "4px" }),
                            indicatorSeparator: () => ({ display: "none" }),
                            option: (styles) => ({ ...styles, fontSize: "14px" }),
                            placeholder: (styles) => ({ ...styles, lineHeight: "18px" }),
                        },
                    };
                    return <Select {...selectProps} />;
                },
                Cell: ({ row: { original } }) => <Route data={original} />,
                tooltipOverlay: (
                    <div>
                        План маршрута
                        <br />
                        Статус
                    </div>
                ),
                sortable: true,
                Sort: ({ column }) => {
                    useEffect(() => {
                        UmamiManager.track("RouteColumnHeaderSortIconClicked");

                        setBlurred(true);

                        return () => setBlurred(false);
                    }, []);
                    return (
                        <div className="p-header__sort">
                            <div
                                className="p-header__sort-item"
                                onClick={() => onSortedChange(column, "to/city", "asc")}
                            >
                                По месту прибытия (по возр.)
                            </div>
                            <div
                                className="p-header__sort-item"
                                onClick={() => onSortedChange(column, "to/city", "desc")}
                            >
                                По месту прибытия (по убыв.)
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: "Дата освобождения",
                accessor: "plannedEndDate",
                testId: "trip-planning-end-dates",
                sortable: true,
                filterable: true,
                filterType: "dateTimeRange",
                Filter: (props) => (
                    <DateRangeColumnFilter
                        {...props}
                        libOptions={{
                            onCalendarOpen: () => setBlurred(true),
                            onCalendarClose: () => setBlurred(false),
                        }}
                    />
                ),
                Cell: ({ row: { original } }) => <EndDates data={original} modal={props.modal} roles={props.roles} />,
                tooltipOverlay: (
                    <div>
                        Плановая дата освобождения
                        <br />
                        Расчетная дата освобождения
                    </div>
                ),
                Sort: ({ column }) => {
                    useEffect(() => {
                        UmamiManager.track("ReleaseDateColumnHeaderSortIconClicked");

                        setBlurred(true);

                        return () => setBlurred(false);
                    }, []);

                    const handleClickWrapper = (callback) => {
                        UmamiManager.track("ReleaseDateColumnHeaderFilterClicked");

                        callback();
                    };

                    return (
                        <div className="p-header__sort">
                            <div
                                className="p-header__sort-item"
                                onClick={() =>
                                    handleClickWrapper(() => onSortedChange(column, "estimatedEndDate", "asc"))
                                }
                            >
                                По дате освобождения (по возр.)
                            </div>
                            <div
                                className="p-header__sort-item"
                                onClick={() =>
                                    handleClickWrapper(() => onSortedChange(column, "estimatedEndDate", "desc"))
                                }
                            >
                                По дате освобождения (по убыв.)
                            </div>
                        </div>
                    );
                },
            },
            {
                Header: "Последние события",
                accessor: "notifications",
                testId: "trip-planning-notifications",
                AdditionalIcon: ({ notificationsFilter, setNotificationsFilter }) => (
                    <NotificationsGear
                        setBlurred={setBlurred}
                        notificationsFilter={notificationsFilter}
                        setNotificationsFilter={setNotificationsFilter}
                    />
                ),
                Cell: ({ value, notificationsFilter }) => (
                    <Notifications data={value} notificationsFilter={notificationsFilter} />
                ),
                tooltipOverlay: "События по машине",
            },
        ];
        return items.map((column) => ({
            ...column,
            width: column.width ?? columnWidths[column.accessor] ?? 150,
        }));
    }, []);

    const instance = useTable(
        {
            ...props,
            columns: tableColumns,
            minRows: 10,
            autoResetPage: false,
            setNotificationsFilter,
            notificationsFilter,
        },
        useResizeColumns,
        useFlexLayout,
        useExpanded,
    );

    const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows, expandedDepth, toggleAllRowsExpanded } =
        instance;

    const { columnResizing } = instance.state;
    useEffect(() => {
        storeColumnWidth(columnResizing);
    }, [columnResizing]);

    const handleTableClickCapture = (event) => {
        if (expandedDepth === 0 || !(event.target instanceof Element)) {
            return;
        }

        if (event.target.closest(".p-expanded, .td-expanded")) {
            return;
        }

        setExpandedRowColumn(undefined);
        toggleAllRowsExpanded(false);
    };

    return (
        <div
            {...getTableProps()}
            className="p-table"
            data-test-id="trip-planning-table"
            ref={scrollRef}
            onClickCapture={handleTableClickCapture}
        >
            <div className="p-table__head">
                {headerGroups.map((headerGroup, i) => {
                    const { key: headerGroupKey, props: headerGroupProps } = splitKeyFromProps(
                        headerGroup.getHeaderGroupProps(),
                    );
                    return (
                        <div key={headerGroupKey ?? i} {...headerGroupProps} className="tr">
                            {headerGroup.headers.map((column, i) => (
                                <TooltipOverlay key={i} overlay={column.tooltipOverlay}>
                                    <HeaderCell column={column} sorted={sorted} />
                                </TooltipOverlay>
                            ))}
                        </div>
                    );
                })}
            </div>
            <div
                className={cn("p-table__body", {
                    "p-table__body--blurred": blurred,
                    "p-table__body--expanded": expandedDepth > 0,
                })}
                {...getTableBodyProps()}
            >
                {rows.map((row, i) => {
                    prepareRow(row);
                    const { key: rowKey, props: rowProps } = splitKeyFromProps(row.getRowProps());
                    return (
                        <Fragment key={rowKey ?? i}>
                            <div {...rowProps} className="tr" data-test-id="trip-planning-row">
                                {row.cells.map((cell, i) => {
                                    const handleCellClick = () => {
                                        if (!cell.column.clickable) {
                                            return;
                                        }
                                        if (row.isExpanded) {
                                            if (cell.column.id === expandedRowColumn) {
                                                setExpandedRowColumn(undefined);
                                                cell.row.toggleRowExpanded();
                                            } else {
                                                ymEvent("TripPlanningTableUsed");
                                                setExpandedRowColumn(cell.column.id);
                                            }
                                        } else {
                                            ymEvent("TripPlanningTableUsed");
                                            setExpandedRowColumn(cell.column.id);
                                            cell.row.toggleRowExpanded();
                                        }
                                    };
                                    const { key: cellKey, props: cellProps } = splitKeyFromProps(cell.getCellProps());

                                    return (
                                        <div
                                            key={cellKey ?? i}
                                            {...cellProps}
                                            onClick={handleCellClick}
                                            className={cn("td", {
                                                "td--clickable": cell.column.clickable,
                                                "td-expanded": row.isExpanded && cell.column.id === expandedRowColumn,
                                            })}
                                            data-test-id={cell.column.testId}
                                        >
                                            {cell.render("Cell")}
                                        </div>
                                    );
                                })}
                            </div>
                            {row.isExpanded && (
                                <ErrorBoundary>
                                    {expandedRowColumn === "car/id" ? (
                                        <ExpandedCar
                                            accessPermits={accessPermits}
                                            data={row.original}
                                            modal={modal}
                                            roles={props.roles}
                                        />
                                    ) : (
                                        <ExpandedRoute
                                            data={row.original}
                                            modal={modal}
                                            organization={props.organization}
                                            roles={props.roles}
                                        />
                                    )}
                                </ErrorBoundary>
                            )}
                        </Fragment>
                    );
                })}
                {rows.length === 0 && !props.loading && <div className="rt-noData">Нет данных для отображения</div>}
            </div>
        </div>
    );
};

export default Table;
